import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AppContext from '../../../context/AppContext';
import MapContext from '../../../context/MapContext';
import CustomInput from './CustomInput';
import PoiManager, {
    formattingPoiType,
    getCatPoiIconName,
    getSearchResultIcon,
    getCategoryName,
    parseBrandType,
} from '../../../manager/PoiManager';
import SearchResultItem from './SearchResultItem';
import { MenuButton } from './MenuButton';
import { Box, Button, ListItemButton, ListItemIcon, Typography } from '@mui/material';
import { ReactComponent as SearchIcon } from '../../../assets/icons/ic_action_search_dark.svg';
import SelectItemBoolean from '../../../frame/components/items/SelectItemBoolean';
import VirtualizedList from '../../../frame/components/VirtualizedList';
import styles from '../search.module.css';
import gStyles from '../../gstylesmenu.module.css';
import { iconPathMap, MAP_VIEW_SEARCH_RESULT } from '../../../map/util/MapManager';
import { searchTypeMap } from '../../../manager/searchConstants';
import Loading from '../../errors/Loading';
import { useGeoLocation } from '../../../util/hooks/useGeoLocation';
import { useElementHeight } from '../../../util/hooks/useElementHeight';
import { usePageTitle } from '../../../util/hooks/usePageTitle';
import { LOCATION_UNAVAILABLE } from '../../../manager/FavoritesManager';
import EmptySearch from '../../errors/EmptySearch';
import { POI_LAYER_ID, SEARCH_LAYER_ID } from '../../../manager/GlobalManager';
import useHashParams from '../../../util/hooks/useHashParams';
import {
    CATEGORY_ICON,
    CATEGORY_TYPE,
    FINAL_POI_ICON_NAME,
    WEB_VISIBLE_LEVEL,
} from '../../../infoblock/components/wpt/WptTagsProvider';
import { clearSearchQuery, getIconByType } from '../../../manager/SearchManager';
import { SEARCH_BRAND } from '../../../manager/searchConstants';
import { parseTagWithLang } from '../../../manager/PoiManager';
import useSearchNav from '../../../util/hooks/search/useSearchNav';
import useSpatialSearch from '../../../util/hooks/search/useSpatialSearch';
import { useTranslation } from 'react-i18next';
import { getMapCenter } from '../../../map/layers/MapStateLayer';
import { useZoomToFit } from '../../../util/hooks/map/useZoomToFit';
import { useNavigate } from 'react-router-dom';

export const ZOOM_ERROR = 'Please zoom in closer';
export const MIN_SEARCH_ZOOM = 8;
const EMPTY_SEARCH_RESULT = 'empty';
// Initial row height estimate used before a row is measured. Real heights are measured per row
// (items vary: 1 line vs name + multi-line address + distance) and cached.
const SEARCH_RESULT_ITEM_HEIGHT = 88;
const SHOW_MORE_ITEM = '__show_more__';

function getRowKey(item, index) {
    if (item === SHOW_MORE_ITEM) return SHOW_MORE_ITEM;
    return item?.id ?? item?.properties?.id ?? index;
}

function getVisibleLevel(item) {
    return item?.properties?.[WEB_VISIBLE_LEVEL] ?? 0;
}

export function searchByWord(searchParams, ctx, loc, baseSearch = false) {
    ctx.setSearchQuery({
        engine: searchParams.engine,
        query: searchParams.query,
        latlng: { lat: loc.lat, lng: loc.lng },
        baseSearch,
    });
}

export function performBaseSearch(searchParams, ctx, loc) {
    searchByWord(searchParams, ctx, loc, true);
}

export function searchByCategory(searchParams, ctx, t, name) {
    // Brand format: type:lang, Category format: just type
    let categoryName = '';
    let lang = null;
    if (searchParams.type) {
        const brandInfo = parseBrandType(searchParams.type);
        if (brandInfo) {
            // Brand search: type format is "brandName:lang"
            categoryName = brandInfo.brandName;
            lang = brandInfo.lang;
        } else {
            // Category search: translate category name
            categoryName = name ?? getCategoryName(searchParams.type, t);
        }
    }

    ctx.setSearchQuery({
        engine: searchParams.engine,
        query: formattingPoiType(categoryName),
        type: searchParams.type,
        lang: lang,
    });
}

export default function SearchResults() {
    const ctx = useContext(AppContext);
    const mtx = useContext(MapContext);
    const { t } = useTranslation();

    const [result, setResult] = useState(null);
    const hash = window.location.hash;
    const [locReady, setLocReady] = useState(false);
    const [errorZoom, setErrorZoom] = useState(null);
    const currentLoc = useGeoLocation(ctx);
    const [listContainerRef, listHeight] = useElementHeight();
    const { zoom } = useHashParams();
    const { classicSearch, setClassicSearch } = useSpatialSearch();
    const navigate = useNavigate();

    const { params, navigateToSearchMenu, navigateToSearchResults, isSearchEqualToUrl, isSearchResultRoute, location } =
        useSearchNav();
    const hasSearchParams = !!(params.type || (params.query && params.query !== ''));
    const categoryName =
        location.state?.categoryName ??
        (result ? (ctx.searchResult?.categoryNames?.[params.type] ?? getCategoryName(params.type, t)) : '');
    const useSpatialSearchResults = !params.type && ctx.spatialSearch;
    const { hasMapView, requestMapViewPop } = useZoomToFit();

    useEffect(() => {
        if ((params.query || params.type) && !isSearchEqualToUrl(ctx.searchQuery)) {
            setResult(null);
        }
    }, [params.engine, params.query, params.type, ctx.searchQuery]);

    useEffect(() => {
        if (hasMapView(MAP_VIEW_SEARCH_RESULT)) {
            requestMapViewPop(MAP_VIEW_SEARCH_RESULT);
        }
    }, []);

    useEffect(() => {
        if (result === EMPTY_SEARCH_RESULT) {
            checkZoomError();
        }
    }, [zoom, result, ctx.searchResult?.tooMany]);

    // Calculate page title based on search params
    const pageTitle = useMemo(() => {
        if (params.query) {
            return params.query;
        } else if (params.type) {
            const brandInfo = parseBrandType(params.type);
            if (brandInfo) {
                return brandInfo.brandName;
            } else {
                return categoryName;
            }
        }
        return null;
    }, [params.engine, params.query, params.type, categoryName]);

    usePageTitle(pageTitle);

    // always hide explore markers when search query is active
    useEffect(() => {
        if (ctx.searchQuery) {
            ctx.setSearchSettings({ ...ctx.searchSettings, showExploreMarkers: false });
        }
    }, [ctx.searchQuery]);

    const calculateIcons = async (features, ctx) => {
        const promises = features?.map(async (f) => {
            if (!f?.properties) return;
            if (f.icon) return;
            const props = f.properties;
            const type = props[CATEGORY_TYPE];
            if (type === searchTypeMap.FAVORITE || type === searchTypeMap.WPT) {
                return;
            }
            if (type === searchTypeMap.POI_TYPE || type === searchTypeMap.POI) {
                const brandRes = parseTagWithLang(props[CATEGORY_ICON]);
                if (brandRes.key === SEARCH_BRAND) {
                    f.icon = await getSearchResultIcon({ result: SEARCH_BRAND, ctx });
                    return;
                }
                const iconName = getCatPoiIconName(props);
                f.icon = await getSearchResultIcon({ result: iconName, ctx });
                f.properties[FINAL_POI_ICON_NAME] = PoiManager.getFinalPoiIconName(f.properties);
            } else {
                const finalIconName = getIconByType(type);
                f.icon = await getSearchResultIcon({
                    result: finalIconName,
                    ctx,
                    iconUrl: iconPathMap[finalIconName],
                });
                f.properties[FINAL_POI_ICON_NAME] = finalIconName;
            }
        });
        await Promise.all(promises);
    };

    const memoizedResult = useMemo(() => {
        if (!currentLoc) return null;
        const { loc } = getLoc();

        if (!loc) return null;

        const features = ctx.searchResult?.features;
        if (!features) {
            return null;
        }

        if (features.length === 0) {
            return EMPTY_SEARCH_RESULT;
        }

        return features.map((f) => ({ ...f }));
    }, [currentLoc, ctx.searchResult]);

    useEffect(() => {
        if (ctx.processingSearch) return;
        if (!memoizedResult) return;
        if (memoizedResult === EMPTY_SEARCH_RESULT) {
            setResult(EMPTY_SEARCH_RESULT);
            return;
        }
        let cancelled = false;
        const updateIcons = async () => {
            const resultWithIcons = [...memoizedResult];
            const visibleToIcon = resultWithIcons.filter((f) => getVisibleLevel(f) <= ctx.searchVisibleLevel);
            await calculateIcons(visibleToIcon, ctx);
            if (!cancelled) {
                setResult({ features: resultWithIcons });
            }
        };

        updateIcons().then();
        return () => {
            cancelled = true;
        };
    }, [memoizedResult, ctx.processingSearch, ctx.searchVisibleLevel]);

    useEffect(() => {
        if (locReady) {
            if (hasSearchParams && (!isSearchEqualToUrl(ctx.searchQuery) || ctx.forceSearch)) {
                if (!isSearchResultRoute) {
                    return;
                }
                const isWordSearch = params.query && params.query !== '' && !params.type;
                if (isWordSearch && (ctx.gpxLoading || ctx.processingGroups)) {
                    return;
                }
                ctx.setProcessingSearch(true);
                // drop the previous results so the spinner stays until the new list is ready (no stale flash)
                setResult(null);
                if (ctx.forceSearch) {
                    ctx.setForceSearch(false);
                }
                if (params.type) {
                    searchByCategory(params, ctx, t, location.state?.categoryName);
                } else {
                    const { loc } = getLoc();
                    if (!loc) return;
                    if (zoom < MIN_SEARCH_ZOOM) {
                        performBaseSearch(params, ctx, loc);
                    } else {
                        searchByWord(params, ctx, loc);
                    }
                }
            }
        }
    }, [locReady, params, ctx.forceSearch, ctx.gpxLoading, ctx.processingGroups]);

    function checkZoomError() {
        if (params.type) {
            setErrorZoom(ctx.searchResult?.tooMany ? ZOOM_ERROR : null);
        } else {
            setErrorZoom(zoom < MIN_SEARCH_ZOOM ? ZOOM_ERROR : null);
        }
    }

    function getLoc() {
        let isUser = false;
        let loc = null;
        if (currentLoc && currentLoc !== LOCATION_UNAVAILABLE) {
            if (ctx.visibleBounds?.contains(currentLoc)) {
                isUser = true;
                loc = currentLoc;
            } else {
                loc = getMapCenter(mtx, hash);
            }
        } else if (currentLoc && currentLoc === LOCATION_UNAVAILABLE) {
            loc = getMapCenter(mtx, hash);
        }
        return { loc, isUser };
    }

    useEffect(() => {
        // hide explore layers
        ctx.setSearchSettings({ ...ctx.searchSettings, showExploreMarkers: false });
    }, []);

    useEffect(() => {
        if (!ctx.searchResult && result !== null) {
            setResult(null);
        }
    }, [ctx.searchResult]);

    function backToMainSearch() {
        if (params.type && location.state?.backToSearchResults) {
            navigate(-1);
            return;
        }
        if (hasMapView(MAP_VIEW_SEARCH_RESULT)) {
            requestMapViewPop(MAP_VIEW_SEARCH_RESULT);
        }
        ctx.setCurrentObjectType(null);
        clearSearchQuery(ctx);
        ctx.setSearchSettings({ ...ctx.searchSettings, showExploreMarkers: true });
        navigateToSearchMenu();
    }

    function getSpatialIssueMessage() {
        if (!useSpatialSearchResults) {
            return null;
        }
        if (ctx.searchResult?.info?.timeout) {
            return t('web:spatial_search_timeout_descr');
        }
        if (ctx.searchResult?.info?.busy) {
            return t('web:spatial_search_busy_descr');
        }

        return null;
    }

    function resulNotPrepared() {
        return hasSearchParams && !ctx.processingSearch && (!result || reopenSearchResult());
    }

    function reopenSearchResult() {
        return result && result !== EMPTY_SEARCH_RESULT && !params.query && !params.type;
    }

    // URL query already changed but the shown result is still the previous search
    const staleResult = (params.query || params.type) && !isSearchEqualToUrl(ctx.searchQuery);

    const maxVisibleLevel = result?.features?.reduce((max, f) => Math.max(max, getVisibleLevel(f)), 0) ?? 0;
    const hasMore =
        ctx.searchVisibleLevel < maxVisibleLevel &&
        (result?.features?.some((item) => item?.properties && getVisibleLevel(item) > ctx.searchVisibleLevel) ?? false);
    const visibleFeatures = useMemo(() => {
        const features =
            result?.features?.filter((item) => item?.properties && getVisibleLevel(item) <= ctx.searchVisibleLevel) ??
            [];
        return useSpatialSearchResults && hasMore ? [...features, SHOW_MORE_ITEM] : features;
    }, [result, ctx.searchVisibleLevel, useSpatialSearchResults, hasMore]);

    const { loc: distanceLoc, isUser } = useMemo(
        () => getLoc(),
        [currentLoc, ctx.visibleBounds, mtx.visibleBboxInfo, hash]
    );

    useEffect(() => {
        if (distanceLoc) {
            setLocReady(true);
        }
    }, [distanceLoc]);

    const showMoreResults = useCallback(() => {
        ctx.setSearchVisibleLevel((prev) => Math.min(prev + 1, maxVisibleLevel));
    }, [maxVisibleLevel]);
    const typeItem = ctx.searchQuery?.type ? POI_LAYER_ID : SEARCH_LAYER_ID;
    const renderSearchItem = useCallback(
        (item, index) => {
            if (item === SHOW_MORE_ITEM) {
                return (
                    <Button
                        id={'se-search-show-more'}
                        className={styles.buttonShowAllExplore}
                        onClick={showMoreResults}
                    >
                        {t('web:show_more')}
                    </Button>
                );
            }

            return (
                <SearchResultItem
                    item={item}
                    index={index}
                    typeItem={typeItem}
                    currentLoc={currentLoc}
                    loc={distanceLoc}
                    isUser={isUser}
                />
            );
        },
        [typeItem, currentLoc, distanceLoc, isUser, showMoreResults]
    );

    return (
        <>
            <CustomInput
                menuButton={<MenuButton needBackButton={true} backToPrevScreen={backToMainSearch} />}
                defaultSearchValue={
                    params?.type ? formattingPoiType(pageTitle) : (ctx.searchQuery?.query ?? (params?.query || ''))
                }
            />
            {ctx.develFeatures && !params.type && (
                <SelectItemBoolean
                    title={t('web:dev_use_old_search')}
                    checked={classicSearch}
                    onToggle={setClassicSearch}
                    boldTitle={false}
                />
            )}
            {ctx.develFeatures && ctx.searchResult?.info && (
                <Typography className={styles.spatialInfo} id={'se-spatial-search-info'}>
                    {Object.entries(ctx.searchResult.info)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(' · ')}
                </Typography>
            )}
            {(ctx.processingSearch || resulNotPrepared() || staleResult) && <Loading />}
            {ctx.searchResult?.typoSuggestion && !ctx.processingSearch && !staleResult && (
                <ListItemButton
                    id={'se-search-typo-suggestion'}
                    className={styles.typoSuggestion}
                    onClick={() => navigateToSearchResults({ query: ctx.searchResult.typoSuggestion })}
                >
                    <ListItemIcon className={styles.typoSuggestionIcon}>
                        <SearchIcon />
                    </ListItemIcon>
                    <Typography noWrap>{ctx.searchResult.typoSuggestion}</Typography>
                </ListItemButton>
            )}
            {(hasSearchParams || result === EMPTY_SEARCH_RESULT) &&
                !ctx.processingSearch &&
                !reopenSearchResult() &&
                !staleResult &&
                (result === EMPTY_SEARCH_RESULT ? (
                    <EmptySearch message={errorZoom ?? getSpatialIssueMessage()} />
                ) : (
                    <Box id={'se-search-results'} ref={listContainerRef} className={gStyles.fillBlock}>
                        <VirtualizedList
                            items={visibleFeatures}
                            renderItem={renderSearchItem}
                            getItemKey={getRowKey}
                            estimatedItemHeight={SEARCH_RESULT_ITEM_HEIGHT}
                            height={listHeight}
                        />
                    </Box>
                ))}
        </>
    );
}
